/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

// Élément HTML personnalisé pour les items de video-banner
class VideoBannerItem extends HTMLElement {
	constructor() {
		super();
		this.video = null;
		this.titleElement = null;
		this.isPlaying = false;

		// Variables pour détecter tap vs swipe
		this.touchStartX = 0;
		this.touchStartY = 0;
		this.touchStartTime = 0;
		this.isTouchMoved = false;

		// Bind methods to preserve context
		this.handleClick = this.handleClick.bind(this);
		this.handleTouchStart = this.handleTouchStart.bind(this);
		this.handleTouchMove = this.handleTouchMove.bind(this);
		this.handleTouchEnd = this.handleTouchEnd.bind(this);
		this.handleVideoEnd = this.handleVideoEnd.bind(this);
	}

	connectedCallback() {
		// Trouver la vidéo et le titre dans cet élément
		this.video = this.querySelector("video");
		this.titleElement = this.querySelector(".video-banner__objectif-title");

		if (this.video) {
			// Configurer la vidéo
			this.video.muted = true;
			this.video.loop = false;
			this.video.controls = false;

			// Ajouter les événements
			this.addEventListener("click", this.handleClick);
			this.addEventListener("touchstart", this.handleTouchStart, {
				passive: true,
			});
			this.addEventListener("touchmove", this.handleTouchMove, {
				passive: true,
			});
			this.addEventListener("touchend", this.handleTouchEnd);
			this.video.addEventListener("ended", this.handleVideoEnd);

			// Marquer comme prêt
			this.setAttribute("data-video-ready", "true");
		}
	}

	disconnectedCallback() {
		// Nettoyer les événements
		this.removeEventListener("click", this.handleClick);
		this.removeEventListener("touchstart", this.handleTouchStart);
		this.removeEventListener("touchmove", this.handleTouchMove);
		this.removeEventListener("touchend", this.handleTouchEnd);

		if (this.video) {
			this.video.removeEventListener("ended", this.handleVideoEnd);
		}
	}

	handleClick(event) {
		// Pour desktop uniquement
		event.preventDefault();
		this.toggleVideo();
	}

	handleTouchStart(event) {
		// Enregistrer la position et le temps de départ
		const touch = event.touches[0];
		this.touchStartX = touch.clientX;
		this.touchStartY = touch.clientY;
		this.touchStartTime = Date.now();
		this.isTouchMoved = false;
	}

	handleTouchMove(event) {
		// Détecter si l'utilisateur fait un swipe
		const touch = event.touches[0];
		const deltaX = Math.abs(touch.clientX - this.touchStartX);
		const deltaY = Math.abs(touch.clientY - this.touchStartY);

		// Si le mouvement est significatif, c'est un swipe
		const swipeThreshold = 10; // pixels
		if (deltaX > swipeThreshold || deltaY > swipeThreshold) {
			this.isTouchMoved = true;
		}
	}

	handleTouchEnd(event) {
		const touchDuration = Date.now() - this.touchStartTime;
		const maxTapDuration = 300; // ms

		// Si c'est un tap court sans mouvement significatif
		if (!this.isTouchMoved && touchDuration < maxTapDuration) {
			event.preventDefault();
			this.toggleVideo();
		}

		// Reset des variables
		this.isTouchMoved = false;
	}

	toggleVideo() {
		if (!this.video) return;

		if (this.isPlaying) {
			this.pauseVideo();
		} else {
			this.playVideo();
		}
	}

	playVideo() {
		if (!this.video) return;

		// Arrêter toutes les autres vidéos
		this.stopOtherVideos();

		// Démarrer cette vidéo
		this.video.currentTime = 0;
		this.video
			.play()
			.then(() => {
				this.isPlaying = true;
				this.hideTitle();
				this.setAttribute("data-playing", "true");
			})
			.catch((error) => {
				console.error("Erreur lors de la lecture de la vidéo:", error);
			});
	}

	pauseVideo() {
		if (!this.video) return;

		this.video.pause();
		this.isPlaying = false;
		this.showTitle();
		this.removeAttribute("data-playing");
	}

	stopOtherVideos() {
		// Trouver tous les autres éléments video-banner-item
		const allVideoItems = document.querySelectorAll("video-banner-item");

		allVideoItems.forEach((item) => {
			if (item !== this && item.video) {
				item.pauseVideo();
			}
		});
	}

	hideTitle() {
		if (this.titleElement) {
			this.titleElement.style.opacity = "0";
			this.titleElement.style.visibility = "hidden";
			this.titleElement.style.transition =
				"opacity 0.3s ease, visibility 0.3s ease";
		}
	}

	showTitle() {
		if (this.titleElement) {
			this.titleElement.style.opacity = "1";
			this.titleElement.style.visibility = "visible";
			this.titleElement.style.transition =
				"opacity 0.3s ease, visibility 0.3s ease";
		}
	}

	handleVideoEnd() {
		this.isPlaying = false;
		this.showTitle();
		this.removeAttribute("data-playing");
	}
}

// Enregistrer l'élément personnalisé
customElements.get("video-banner-item") ||
	customElements.define("video-banner-item", VideoBannerItem);

class ShopTheLook extends HTMLElement {
	constructor() {
		super();
		this.productContent = this.querySelector(
			".page__header-content--product-item-content",
		);
		this.icon = this.querySelector(".icon--plus");

		if (!this.productContent || !this.icon) {
			console.error("ShopTheLook: Could not find required elements.");
			return;
		}

		// "Bind" des méthodes
		this.show = this.show.bind(this);
		this.hide = this.hide.bind(this);
		this.toggle = this.toggle.bind(this);
		this.closeOnOutsideClick = this.closeOnOutsideClick.bind(this);
	}

	connectedCallback() {
		// Pour les utilisateurs de souris
		this.addEventListener("mouseenter", this.show);
		this.addEventListener("mouseleave", this.hide);

		// Pour les utilisateurs d'écrans tactiles (le clic est intercepté sur l'icône)
		this.icon.addEventListener("click", this.toggle);
	}

	disconnectedCallback() {
		this.removeEventListener("mouseenter", this.show);
		this.removeEventListener("mouseleave", this.hide);
		this.icon.removeEventListener("click", this.toggle);
		document.removeEventListener("click", this.closeOnOutsideClick);
	}

	toggle(event) {
		event.stopPropagation(); // Empêche le clic de se propager au document
		const isVisible = this.hasAttribute("open");

		// Ferme les autres popups
		document.querySelectorAll("shop-the-look[open]").forEach((el) => {
			if (el !== this) {
				el.hide();
			}
		});

		if (isVisible) {
			this.hide();
		} else {
			this.show();
		}
	}

	show() {
		if (this.hasAttribute("open")) return; // Déjà ouvert

		// Ferme les autres popups
		document.querySelectorAll("shop-the-look[open]").forEach((el) => {
			if (el !== this) {
				el.hide();
			}
		});

		this.productContent.style.display = "flex";
		setTimeout(() => {
			this.productContent.style.opacity = "1";
		}, 10);
		this.setAttribute("open", "");

		// Ajoute un écouteur pour fermer en cliquant à l'extérieur (surtout pour le tactile)
		setTimeout(
			() => document.addEventListener("click", this.closeOnOutsideClick),
			0,
		);
	}

	hide() {
		if (!this.hasAttribute("open")) return;

		this.productContent.style.opacity = "0";
		setTimeout(() => {
			this.productContent.style.display = "none";
		}, 300); // Doit correspondre à la durée de la transition
		this.removeAttribute("open");
		document.removeEventListener("click", this.closeOnOutsideClick);
	}

	closeOnOutsideClick(event) {
		// Ne ferme que si le clic est en dehors de l'élément actuel
		if (!this.contains(event.target)) {
			this.hide();
		}
	}
}

customElements.get("shop-the-look") ||
	customElements.define("shop-the-look", ShopTheLook);

class ImageWithTextTabsSection extends HTMLElement {
	constructor() {
		super();

		this.tabLinks = this.querySelectorAll(".image-with-text__tab-link");
		this.tabContents = this.querySelectorAll(".image-with-text__tab-content");
		this.imageItems = this.querySelectorAll(".image-with-text__image-item");

		this.tabLinks.forEach((link) => {
			link.addEventListener("click", this.onTabClick.bind(this));
		});
	}

	onTabClick(event) {
		event.preventDefault();
		const targetId = event.currentTarget.dataset.tabFor;

		this.tabLinks.forEach((link) => {
			link.classList.remove("is-active");
		});

		this.tabContents.forEach((content) => {
			content.classList.remove("is-active");
		});

		this.imageItems.forEach((image) => {
			image.classList.remove("is-active");
		});

		event.currentTarget.classList.add("is-active");
		document.getElementById(targetId)?.classList.add("is-active");

		const imageTarget = this.querySelector(`[data-image-for="${targetId}"]`);
		imageTarget?.classList.add("is-active");
	}
}

customElements.get("image-with-text-tabs-section") ||
	customElements.define(
		"image-with-text-tabs-section",
		ImageWithTextTabsSection,
	);

// Ouvrir automatiquement le mini-cart (cart drawer) sur la homepage si ?cart=true
document.addEventListener("DOMContentLoaded", function () {
	try {
		if (window?.theme?.pageType !== "index") return;

		var hasCartParam = new URLSearchParams(window.location.search).get("cart");
		if (hasCartParam !== "true") return;

		// Si le thème est configuré avec un mini-cart en drawer
		if (window?.theme?.cartType === "drawer") {
			// Déclenche l'ouverture du mini-cart en simulant un clic sur le toggle si présent
			var toggle = document.querySelector('[data-action="toggle-mini-cart"]');
			if (toggle) {
				toggle.click();
			} else {
				// Fallback: forcer un refresh puis tenter d'ouvrir via événement interne
				document.documentElement.dispatchEvent(
					new CustomEvent("cart:refresh", { bubbles: true }),
				);
				// Essayez de forcer le focus du drawer si disponible plus tard
				setTimeout(function () {
					var toggleLater = document.querySelector(
						'[data-action="toggle-mini-cart"]',
					);
					if (toggleLater) toggleLater.click();
				}, 300);
			}
		}
	} catch (e) {
		// silencieux
	}
});

// Mosaic Slider Component
class MosaicSlider extends HTMLElement {
	constructor() {
		super();
		this.currentSlide = 0;
		this.totalSlides = 0;
		this.autoplayInterval = null;
		this.isTransitioning = false;

		// Touch handling
		this.touchStartX = 0;
		this.touchEndX = 0;
		this.touchStartY = 0;
		this.touchEndY = 0;

		// Bind methods
		this.goToSlide = this.goToSlide.bind(this);
		this.nextSlide = this.nextSlide.bind(this);
		this.prevSlide = this.prevSlide.bind(this);
		this.handleTouchStart = this.handleTouchStart.bind(this);
		this.handleTouchMove = this.handleTouchMove.bind(this);
		this.handleTouchEnd = this.handleTouchEnd.bind(this);
		this.handleKeydown = this.handleKeydown.bind(this);
	}

	connectedCallback() {
		// Get elements
		this.track = this.querySelector(".mosaic-slider__track");
		this.slides = this.querySelectorAll(".mosaic-slider__slide");
		this.prevButton = this.querySelector(".mosaic-slider__arrow--prev");
		this.nextButton = this.querySelector(".mosaic-slider__arrow--next");
		this.dots = this.querySelectorAll(".mosaic-slider__dot");

		if (!this.track || !this.slides.length) return;

		this.totalSlides = this.slides.length;

		// Get settings from data attributes
		this.autoplay = this.dataset.autoplay === "true";
		this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed) || 5;
		this.showArrows = this.dataset.showArrows === "true";
		this.showDots = this.dataset.showDots === "true";

		// Set up event listeners
		if (this.prevButton) {
			this.prevButton.addEventListener("click", this.prevSlide);
		}

		if (this.nextButton) {
			this.nextButton.addEventListener("click", this.nextSlide);
		}

		this.dots.forEach((dot, index) => {
			dot.addEventListener("click", () => this.goToSlide(index));
		});

		// Touch events for swipe
		this.track.addEventListener("touchstart", this.handleTouchStart, {
			passive: true,
		});
		this.track.addEventListener("touchmove", this.handleTouchMove, {
			passive: false,
		});
		this.track.addEventListener("touchend", this.handleTouchEnd, {
			passive: true,
		});

		// Keyboard navigation
		this.addEventListener("keydown", this.handleKeydown);

		// Initialize
		this.goToSlide(0);

		// Start autoplay if enabled
		if (this.autoplay && this.totalSlides > 1) {
			this.startAutoplay();

			// Pause on hover
			this.addEventListener("mouseenter", () => this.stopAutoplay());
			this.addEventListener("mouseleave", () => this.startAutoplay());
		}

		// Handle visibility change (pause when tab is hidden)
		document.addEventListener("visibilitychange", () => {
			if (document.hidden) {
				this.stopAutoplay();
			} else if (this.autoplay && this.totalSlides > 1) {
				this.startAutoplay();
			}
		});
	}

	disconnectedCallback() {
		this.stopAutoplay();

		if (this.prevButton) {
			this.prevButton.removeEventListener("click", this.prevSlide);
		}

		if (this.nextButton) {
			this.nextButton.removeEventListener("click", this.nextSlide);
		}

		this.dots.forEach((dot, index) => {
			dot.removeEventListener("click", () => this.goToSlide(index));
		});

		this.track.removeEventListener("touchstart", this.handleTouchStart);
		this.track.removeEventListener("touchmove", this.handleTouchMove);
		this.track.removeEventListener("touchend", this.handleTouchEnd);
		this.removeEventListener("keydown", this.handleKeydown);
	}

	goToSlide(index, skipTransition = false) {
		if (this.isTransitioning && !skipTransition) return;

		// Clamp index
		if (index < 0) {
			index = this.totalSlides - 1;
		} else if (index >= this.totalSlides) {
			index = 0;
		}

		this.currentSlide = index;
		this.isTransitioning = true;

		// Update track position
		const translateX = -index * 100;
		this.track.style.transform = `translateX(${translateX}%)`;

		// Update dots
		this.dots.forEach((dot, i) => {
			if (i === index) {
				dot.classList.add("is-active");
			} else {
				dot.classList.remove("is-active");
			}
		});

		// Update arrow states
		if (this.prevButton && this.nextButton) {
			this.prevButton.disabled = false;
			this.nextButton.disabled = false;
		}

		// Allow next transition after animation
		setTimeout(() => {
			this.isTransitioning = false;
		}, 500);

		// Reset autoplay timer
		if (this.autoplay) {
			this.stopAutoplay();
			this.startAutoplay();
		}
	}

	nextSlide() {
		this.goToSlide(this.currentSlide + 1);
	}

	prevSlide() {
		this.goToSlide(this.currentSlide - 1);
	}

	startAutoplay() {
		this.stopAutoplay();
		this.autoplayInterval = setInterval(() => {
			this.nextSlide();
		}, this.autoplaySpeed * 1000);
	}

	stopAutoplay() {
		if (this.autoplayInterval) {
			clearInterval(this.autoplayInterval);
			this.autoplayInterval = null;
		}
	}

	handleTouchStart(event) {
		this.touchStartX = event.touches[0].clientX;
		this.touchStartY = event.touches[0].clientY;
	}

	handleTouchMove(event) {
		// Prevent vertical scrolling while swiping horizontally
		const touchMoveX = event.touches[0].clientX;
		const touchMoveY = event.touches[0].clientY;
		const diffX = Math.abs(touchMoveX - this.touchStartX);
		const diffY = Math.abs(touchMoveY - this.touchStartY);

		if (diffX > diffY) {
			event.preventDefault();
		}
	}

	handleTouchEnd(event) {
		this.touchEndX = event.changedTouches[0].clientX;
		this.touchEndY = event.changedTouches[0].clientY;
		this.handleSwipe();
	}

	handleSwipe() {
		const diffX = this.touchEndX - this.touchStartX;
		const diffY = Math.abs(this.touchEndY - this.touchStartY);
		const minSwipeDistance = 50;

		// Only handle horizontal swipes
		if (Math.abs(diffX) > minSwipeDistance && Math.abs(diffX) > diffY) {
			if (diffX > 0) {
				// Swipe right - go to previous slide
				this.prevSlide();
			} else {
				// Swipe left - go to next slide
				this.nextSlide();
			}
		}
	}

	handleKeydown(event) {
		if (event.key === "ArrowLeft") {
			event.preventDefault();
			this.prevSlide();
		} else if (event.key === "ArrowRight") {
			event.preventDefault();
			this.nextSlide();
		}
	}
}

// Register custom element
customElements.get("mosaic-slider") ||
	customElements.define("mosaic-slider", MosaicSlider);

// Sommaire articles: scroll lissé avec offset header/announcement
(function () {
	function getCssPx(varName) {
		try {
			var v = getComputedStyle(document.documentElement)
				.getPropertyValue(varName)
				.trim();
			if (!v) return 0;
			if (v.endsWith("px")) return parseFloat(v);
			var n = parseFloat(v);
			return isNaN(n) ? 0 : n;
		} catch (e) {
			return 0;
		}
	}
	function getOffset() {
		return (
			getCssPx("--announcement-bar-height") + getCssPx("--header-height") + 10
		); // marge
	}
	function scrollToId(id, replaceHash) {
		var el = document.getElementById(id);
		if (!el) return;
		var rect = el.getBoundingClientRect();
		var scrollTop =
			window.pageYOffset || document.documentElement.scrollTop || 0;
		var target = rect.top + scrollTop - getOffset();
		window.scrollTo({ top: Math.max(target, 0), behavior: "smooth" });
		if (replaceHash) {
			try {
				history.replaceState(null, "", "#" + id);
			} catch (e) {}
		}
	}
	document.addEventListener("click", function (e) {
		var a =
			e.target.closest && e.target.closest('.blog-summary__list a[href^="#"]');
		if (!a) return;
		var hash = a.getAttribute("href");
		if (!hash || hash.length < 2) return;
		e.preventDefault();
		scrollToId(hash.slice(1), true);
	});
	// Ajuste après chargement si un hash existe déjà
	window.addEventListener("load", function () {
		if (location.hash && location.hash.length > 1) {
			var id = location.hash.slice(1);
			// Petit délai pour laisser le layout se stabiliser
			setTimeout(function () {
				scrollToId(id, false);
			}, 0);
		}
	});
})();

// ========================================
// Widget d'abonnement
// ========================================
class SubscriptionWidget extends HTMLElement {
	constructor() {
		super();
		this.onVariantChange = this.onVariantChange.bind(this);
		this.onProductRerender = this.onProductRerender.bind(this);
	}

	connectedCallback() {
		// Déplacer la modal vers le body pour éviter les problèmes de stacking context
		this.moveModalToBody();

		// Écouter les changements sur les inputs de type d'achat (one-time vs subscription)
		this.querySelectorAll('input[name="purchase_type"]').forEach((input) => {
			input.addEventListener("change", this.onPurchaseTypeChange.bind(this));
		});

		// Écouter les changements sur le select de fréquence d'abonnement
		const frequencySelect = this.querySelector("#subscription-frequencies");
		if (frequencySelect) {
			frequencySelect.addEventListener(
				"change",
				this.onSellingPlanChange.bind(this),
			);
		}

		// Écouter les changements de variante pour synchroniser avec le variant picker
		const productFormElement = this.closest("product-form");
		if (productFormElement) {
			const form = productFormElement.querySelector("form");
			if (form) {
				form.addEventListener("variant:change", this.onVariantChange);
				// Sauvegarder la référence pour le cleanup
				this._form = form;
			}
		}

		// Écouter les événements product:rerendered pour se synchroniser
		const productRerender = document.querySelector("product-rerender");
		if (productRerender) {
			productRerender.addEventListener(
				"product:rerendered",
				this.onProductRerender,
			);
			// Sauvegarder la référence pour le cleanup
			this._productRerender = productRerender;
		}

		// Forcer l'achat unique pour les packs
		this.checkPackProduct();

		// Initialiser l'URL au chargement si une option est déjà sélectionnée
		this.initializeUrl();
	}

	disconnectedCallback() {
		if (this._form) {
			this._form.removeEventListener("variant:change", this.onVariantChange);
		}

		if (this._productRerender) {
			this._productRerender.removeEventListener(
				"product:rerendered",
				this.onProductRerender,
			);
		}

		// Supprimer la modal du body lors de la déconnexion
		if (this._movedModal && this._movedModal.parentNode) {
			this._movedModal.parentNode.removeChild(this._movedModal);
		}
	}

	moveModalToBody() {
		// Trouver la modal d'abonnement dans le parent
		const parentContainer = this.closest(
			".product-form__info-item--subscription",
		);
		if (!parentContainer) return;

		const modal = parentContainer.querySelector(
			'.modal[id^="modal-subscription-advantages-"]',
		);
		if (modal && modal.parentNode !== document.body) {
			// Déplacer la modal à la fin du body
			document.body.appendChild(modal);
			// Sauvegarder la référence pour le cleanup
			this._movedModal = modal;
		}
	}

	checkPackProduct() {
		const subscriptionPurchase = this.querySelector(
			".ax-subscription-purchase",
		);
		const oneTimeInput = this.querySelector("#one-time-purchase");

		if (!subscriptionPurchase && oneTimeInput) {
			oneTimeInput.checked = true;

			const radioVisual = oneTimeInput.parentElement.querySelector(".ax-radio");
			if (radioVisual) {
				radioVisual.classList.add("ax-radio--checked");
			}

			this.updateFormInputs(false, null);
		}
	}

	async initializeUrl() {
		const subscriptionPurchase = this.querySelector(
			".ax-subscription-purchase",
		);
		if (!subscriptionPurchase) {
			return;
		}

		const url = new URL(window.location.href);
		const sellingPlanId = url.searchParams.get("selling_plan");

		if (sellingPlanId) {
			const subscriptionInput = this.querySelector("#subscription-purchase");
			const oneTimeInput = this.querySelector("#one-time-purchase");
			const sellingPlanSelect = this.querySelector("#subscription-frequencies");

			if (subscriptionInput && !subscriptionInput.checked) {
				subscriptionInput.checked = true;
				if (oneTimeInput) oneTimeInput.checked = false;

				this.updateUIState(true);
			}

			if (sellingPlanSelect) {
				const optionToSelect = sellingPlanSelect.querySelector(
					`option[data-selling-plan-id="${sellingPlanId}"]`,
				);
				if (optionToSelect) {
					sellingPlanSelect.value = optionToSelect.value;
				}
			}

			this.updateFormInputs(true, sellingPlanId);
		} else {
			const subscriptionInput = this.querySelector("#subscription-purchase");
			if (subscriptionInput && subscriptionInput.checked) {
				const currentSellingPlan = this.getCurrentSellingPlan();
				if (currentSellingPlan && currentSellingPlan.id) {
					this.updateUrlWithSellingPlan(currentSellingPlan.id);
				}
			}
		}
	}

	updateUIState(isSubscription) {
		const subscriptionInput = this.querySelector("#subscription-purchase");
		const oneTimeInput = this.querySelector("#one-time-purchase");

		if (subscriptionInput) {
			const radioVisual =
				subscriptionInput.parentElement.querySelector(".ax-radio");
			if (radioVisual) {
				radioVisual.classList.toggle("ax-radio--checked", isSubscription);
			}
		}

		if (oneTimeInput) {
			const radioVisual = oneTimeInput.parentElement.querySelector(".ax-radio");
			if (radioVisual) {
				radioVisual.classList.toggle("ax-radio--checked", !isSubscription);
			}
		}
	}

	async onPurchaseTypeChange(event) {
		const input = event.target;
		const isSubscription =
			input.value === "subscription" || input.id === "subscription-purchase";

		try {
			this.setSubmitButtonsBusy(true);

			let sellingPlanId = null;
			if (isSubscription) {
				const currentPlan = this.getCurrentSellingPlan();
				if (currentPlan && currentPlan.id) {
					sellingPlanId = currentPlan.id;
				} else {
					const subscriptionInput = this.querySelector(
						"#subscription-purchase",
					);
					sellingPlanId = subscriptionInput?.getAttribute(
						"data-selling-plan-id",
					);
				}
			}

			this.updateFormInputs(isSubscription, sellingPlanId);
			this.updateUIState(isSubscription);
			this.updateUrl(isSubscription);

			await this.refreshProductContent(isSubscription, sellingPlanId);

			this.dispatchEvent(
				new CustomEvent("subscription:purchase-type-change", {
					detail: {
						isSubscription: isSubscription,
						purchaseType: input.value,
					},
					bubbles: true,
				}),
			);
		} catch (error) {
			console.error("Error in onPurchaseTypeChange:", error);
		} finally {
			this.setSubmitButtonsBusy(false);
		}
	}

	async onSellingPlanChange(event) {
		const select = event.target;
		const selectedOption = select.selectedOptions[0];

		if (selectedOption) {
			const sellingPlanId = selectedOption.getAttribute("data-selling-plan-id");

			try {
				this.setSubmitButtonsBusy(true);

				this.updateFormInputs(true, sellingPlanId);
				this.updateUrlWithSellingPlan(sellingPlanId);

				await this.refreshProductContent(true, sellingPlanId);

				this.dispatchEvent(
					new CustomEvent("subscription:selling-plan-change", {
						detail: {
							sellingPlanId: sellingPlanId,
							sellingPlanName: selectedOption.getAttribute(
								"data-selling-plan-name",
							),
						},
						bubbles: true,
					}),
				);
			} catch (error) {
				console.error("Error in onSellingPlanChange:", error);
			} finally {
				this.setSubmitButtonsBusy(false);
			}
		}
	}

	async onVariantChange(event) {
		if (event.detail && event.detail.variant) {
			const currentPurchaseType = this.getCurrentPurchaseType();
			if (currentPurchaseType === "subscription") {
				const currentSellingPlan = this.getCurrentSellingPlan();
				if (currentSellingPlan && currentSellingPlan.id) {
					setTimeout(async () => {
						try {
							this.setSubmitButtonsBusy(true);
							this.updateFormInputs(true, currentSellingPlan.id);
							this.updateUrlWithSellingPlan(currentSellingPlan.id);
							await this.refreshProductContent(true, currentSellingPlan.id);
						} catch (error) {
							console.error("Error in onVariantChange:", error);
						} finally {
							this.setSubmitButtonsBusy(false);
						}
					}, 100);
				}
			}
		}
	}

	onProductRerender(event) {
		if (event.detail && !event.detail.productChange) {
			const url = new URL(window.location.href);
			const sellingPlanId = url.searchParams.get("selling_plan");
			const currentPurchaseType = this.getCurrentPurchaseType();

			if (sellingPlanId && currentPurchaseType !== "subscription") {
				this.initializeUrl();
			} else if (!sellingPlanId && currentPurchaseType === "subscription") {
				const currentSellingPlan = this.getCurrentSellingPlan();
				if (currentSellingPlan && currentSellingPlan.id) {
					this.updateUrlWithSellingPlan(currentSellingPlan.id);
				}
			}
		}
	}

	updateUrl(isSubscription) {
		const url = new URL(window.location.href);

		if (isSubscription) {
			const subscriptionInput = this.querySelector("#subscription-purchase");
			const defaultSellingPlanId = subscriptionInput?.getAttribute(
				"data-selling-plan-id",
			);

			if (defaultSellingPlanId) {
				url.searchParams.set("selling_plan", defaultSellingPlanId);
			}
		} else {
			url.searchParams.delete("selling_plan");
		}

		window.history.replaceState({ path: url.toString() }, "", url.toString());
	}

	updateUrlWithSellingPlan(sellingPlanId) {
		const url = new URL(window.location.href);

		if (sellingPlanId) {
			url.searchParams.set("selling_plan", sellingPlanId);
		} else {
			url.searchParams.delete("selling_plan");
		}

		window.history.replaceState({ path: url.toString() }, "", url.toString());
	}

	getCurrentPurchaseType() {
		const oneTimeInput = this.querySelector("#one-time-purchase");
		const subscriptionInput = this.querySelector("#subscription-purchase");

		if (subscriptionInput && subscriptionInput.checked) {
			return "subscription";
		} else if (oneTimeInput && oneTimeInput.checked) {
			return "one-time";
		}

		return null;
	}

	getCurrentSellingPlan() {
		const select = this.querySelector("#subscription-frequencies");
		if (select && select.selectedOptions.length > 0) {
			const selectedOption = select.selectedOptions[0];
			return {
				id: selectedOption.getAttribute("data-selling-plan-id"),
				name: selectedOption.getAttribute("data-selling-plan-name"),
			};
		}

		return null;
	}

	async fetchContentForSubscription(isSubscription, sellingPlanId = null) {
		const sectionId = this.getAttribute("data-section");
		const url = new URL(window.location.href);

		const productUrl = url.origin + url.pathname;
		const params = new URLSearchParams();

		let variantId = url.searchParams.get("variant");
		if (!variantId) {
			const productFormElement =
				this.closest("product-form") || document.querySelector("product-form");
			if (productFormElement) {
				const form = productFormElement.querySelector("form");
				const variantInput = form?.querySelector('input[name="id"]');
				if (variantInput && variantInput.value) {
					variantId = variantInput.value;
				}
			}
		}

		if (variantId) {
			params.set("variant", variantId);
		}

		if (isSubscription && sellingPlanId) {
			params.set("selling_plan", sellingPlanId);
		}

		if (sectionId) {
			params.set("section_id", sectionId);
		}

		const fetchUrl = `${productUrl}?${params.toString()}`;

		try {
			const response = await fetch(fetchUrl);
			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}
			return await response.text();
		} catch (error) {
			console.error("Error fetching subscription content:", error);
			return null;
		}
	}

	async refreshProductContent(isSubscription, sellingPlanId = null) {
		try {
			const htmlContent = await this.fetchContentForSubscription(
				isSubscription,
				sellingPlanId,
			);

			if (htmlContent) {
				const newContent = document
					.createRange()
					.createContextualFragment(htmlContent);

				const productFormElement =
					this.closest("product-form") ||
					document.querySelector("product-form");

				if (productFormElement) {
					// Chercher le vrai élément <form> à l'intérieur
					const form = productFormElement.querySelector("form");

					if (form) {
						form.dispatchEvent(
							new CustomEvent("product:rerender", {
								detail: {
									htmlFragment: newContent,
									productChange: false,
								},
							}),
						);

						setTimeout(() => {
							this.updateFormInputs(isSubscription, sellingPlanId);
						}, 100);
					} else {
						this.updateFormInputs(isSubscription, sellingPlanId);
					}
				} else {
					this.updateFormInputs(isSubscription, sellingPlanId);
				}
			}
		} catch (error) {
			console.error("Error refreshing product content:", error);
			throw error;
		}
	}

	setSubmitButtonsBusy(isBusy) {
		const productFormElement =
			this.closest("product-form") || document.querySelector("product-form");

		if (productFormElement) {
			// Chercher le vrai élément <form> à l'intérieur du custom element product-form
			const form = productFormElement.querySelector("form");

			if (form && form.elements) {
				const submitButtons = Array.from(form.elements).filter(
					(button) => button.type === "submit",
				);

				submitButtons.forEach((submitButton) => {
					if (isBusy) {
						submitButton.setAttribute("aria-busy", "true");
					} else {
						submitButton.removeAttribute("aria-busy");
					}
				});
			}
		}
	}

	updateFormInputs(isSubscription, sellingPlanId = null) {
		const productFormElement =
			this.closest("product-form") || document.querySelector("product-form");

		if (productFormElement) {
			// Chercher le vrai élément <form> à l'intérieur du custom element product-form
			const form = productFormElement.querySelector("form");

			if (form) {
				let sellingPlanInput = form.querySelector('input[name="selling_plan"]');

				if (!sellingPlanInput) {
					sellingPlanInput = document.createElement("input");
					sellingPlanInput.type = "hidden";
					sellingPlanInput.name = "selling_plan";
					form.appendChild(sellingPlanInput);
				}

				const targetValue =
					isSubscription && sellingPlanId ? sellingPlanId : "";
				sellingPlanInput.value = targetValue;
				sellingPlanInput.setAttribute("value", targetValue);
			}
		}
	}
}

// Enregistrer le web component
if (!window.customElements.get("subscription-widget")) {
	window.customElements.define("subscription-widget", SubscriptionWidget);
}

// ========================================
// Bouton sticky pour la fiche produit
// ========================================
(function () {
	"use strict";

	// Vérifier si on est sur une page produit
	if (window.theme?.pageType !== "product") {
		return;
	}

	let originalButton = null;
	let stickyButtonWrapper = null;
	let stickyButtonContent = null;
	let stickyPopin = null;
	let stickyPopinTitle = null;
	let stickyPopinFormat = null;
	let stickyPopinPrice = null;
	let stickyPopinImage = null;
	let isInitialized = false;
	let scrollTimeout = null;
	let isAnimating = false;

	function findOriginalButton() {
		// Chercher le bouton dans product-form__buy-button-wrapper (avec quantity selector)
		const buyButtonWrapper = document.querySelector(
			".product-form__buy-button-wrapper",
		);
		if (buyButtonWrapper) {
			const primaryAction = buyButtonWrapper.querySelector(
				".product-form__primary-action",
			);
			if (primaryAction) {
				return primaryAction;
			}
		}

		// Sinon chercher dans product-form__primary-action (sans quantity selector)
		const primaryAction = document.querySelector(
			".product-form__primary-action:not([data-quantity-inline])",
		);
		if (primaryAction) {
			return primaryAction;
		}

		return null;
	}

	function findStickyButton() {
		return document.querySelector(".product-form__sticky-button-wrapper");
	}

	function updateStickyPopinInfo() {
		if (!stickyButtonWrapper) return;

		// Récupérer les informations depuis les attributs data ou depuis le DOM
		const productTitle =
			stickyButtonWrapper.getAttribute("data-product-title") || "";
		const productFormat =
			stickyButtonWrapper.getAttribute("data-product-format") || "";

		// Récupérer le prix depuis le DOM (il peut changer avec la variante)
		// Priorité : prix en promotion (.price--highlight.price--primary) puis prix normal (.price.price--primary)
		const priceList = document.querySelector(
			".product-meta__price-list[data-price]",
		);
		let productPrice = "";
		if (priceList) {
			const highlightPrice = priceList.querySelector(
				".price--highlight.price--primary",
			);
			const normalPrice = priceList.querySelector(".price.price--primary");
			const priceElement =
				highlightPrice || normalPrice || priceList.querySelector(".price");
			if (priceElement) {
				// Nettoyer le texte pour enlever les éléments visuellement cachés
				const clonedPrice = priceElement.cloneNode(true);
				const hiddenElements = clonedPrice.querySelectorAll(".visually-hidden");
				hiddenElements.forEach(function (el) {
					el.remove();
				});
				productPrice = clonedPrice.textContent.trim();
			}
		}

		// Fallback sur l'attribut data si le prix n'est pas trouvé
		if (!productPrice) {
			productPrice =
				stickyButtonWrapper.getAttribute("data-product-price") || "";
		}

		// Mettre à jour les éléments de la popin
		if (stickyPopinTitle) {
			stickyPopinTitle.textContent = productTitle;
		}

		if (stickyPopinFormat) {
			if (productFormat) {
				stickyPopinFormat.textContent = productFormat;
				stickyPopinFormat.style.display = "";
			} else {
				stickyPopinFormat.style.display = "none";
			}
		}

		if (stickyPopinPrice) {
			stickyPopinPrice.textContent = productPrice;
		}
	}

	function isElementAboveViewport(el) {
		if (!el) return false;

		const rect = el.getBoundingClientRect();

		// Retourne true si l'élément est au-dessus du viewport
		// (l'utilisateur a scrollé en dessous du bouton)
		return rect.bottom < 0;
	}

	function isElementVisibleOrAbove(el) {
		if (!el) return false;

		const rect = el.getBoundingClientRect();
		const windowHeight =
			window.innerHeight || document.documentElement.clientHeight;

		// Retourne true si l'élément est visible dans le viewport
		// OU s'il est en dessous du viewport (l'utilisateur est au-dessus)
		return (
			(rect.top < windowHeight && rect.bottom > 0) || rect.top >= windowHeight
		);
	}

	function isFooterVisible() {
		const footer = document.querySelector("footer.footer");
		if (!footer) return false;

		const rect = footer.getBoundingClientRect();
		const windowHeight =
			window.innerHeight || document.documentElement.clientHeight;

		// Vérifier si le footer est visible dans le viewport (même partiellement)
		return rect.top < windowHeight && rect.bottom > 0;
	}

	function cloneButtonContent() {
		if (!originalButton) return;

		// Cloner pour desktop (popin)
		const desktopContent = stickyPopin
			? stickyPopin.querySelector(".product-form__sticky-button-content")
			: null;
		// Cloner pour mobile
		const mobileContent = document.querySelector(
			".product-form__sticky-button-mobile .product-form__sticky-button-content",
		);

		const containersToUpdate = [];
		if (desktopContent) containersToUpdate.push(desktopContent);
		if (mobileContent) containersToUpdate.push(mobileContent);

		containersToUpdate.forEach(function (container) {
			if (!container) return;

			// Vider le contenu existant
			container.innerHTML = "";

			// Cloner le contenu du bouton original
			const clone = originalButton.cloneNode(true);
			container.appendChild(clone);

			// Réinitialiser les web components personnalisés si nécessaire
			const buyButtons = clone.querySelectorAll("buy-button");
			buyButtons.forEach(function (buyButton) {
				// Les web components doivent être réinitialisés après le clonage
				// Le thème devrait les réinitialiser automatiquement, mais on peut forcer si nécessaire
			});

			// Gérer les clics sur les boutons clonés pour déclencher le bouton original
			const form = document.querySelector("form.product-form");
			if (form) {
				// Gérer les boutons submit directs
				const clonedSubmitButtons = clone.querySelectorAll(
					'button[type="submit"]',
				);
				clonedSubmitButtons.forEach(function (clonedButton) {
					clonedButton.addEventListener("click", function (e) {
						e.preventDefault();
						e.stopPropagation();
						const originalSubmitButton = originalButton.querySelector(
							'button[type="submit"]',
						);
						if (originalSubmitButton) {
							originalSubmitButton.click();
						}
					});
				});

				// Gérer les web components buy-button
				const clonedBuyButtons = clone.querySelectorAll("buy-button");
				clonedBuyButtons.forEach(function (clonedBuyButton) {
					const buttonInside = clonedBuyButton.querySelector(
						'button[type="submit"]',
					);
					if (buttonInside) {
						buttonInside.addEventListener("click", function (e) {
							e.preventDefault();
							e.stopPropagation();
							const originalBuyButton =
								originalButton.querySelector("buy-button");
							if (originalBuyButton) {
								const originalButtonInside = originalBuyButton.querySelector(
									'button[type="submit"]',
								);
								if (originalButtonInside) {
									originalButtonInside.click();
								}
							}
						});
					}
				});
			}
		});
	}

	function updateStickyButton() {
		if (!originalButton || !stickyButtonWrapper || isAnimating) return;

		const isAboveViewport = isElementAboveViewport(originalButton);
		const footerVisible = isFooterVisible();
		const popin = stickyButtonWrapper.querySelector(
			".product-form__sticky-popin",
		);
		const mobileButton = stickyButtonWrapper.querySelector(
			".product-form__sticky-button-mobile",
		);
		const isCurrentlyVisible =
			stickyButtonWrapper.style.display !== "none" &&
			stickyButtonWrapper.style.display !== "";

		// Afficher le sticky uniquement si le bouton original est AU-DESSUS du viewport
		// (l'utilisateur a scrollé en dessous du bouton) ET le footer n'est pas visible
		// Masquer si le bouton original est visible ou en dessous (l'utilisateur est au-dessus) OU si le footer est visible
		if ((!isAboveViewport || footerVisible) && isCurrentlyVisible) {
			// Le bouton original est visible ou le footer est visible, cacher le sticky avec animation de sortie
			isAnimating = true;

			if (popin && !popin.classList.contains("is-exiting")) {
				popin.classList.add("is-exiting");
				setTimeout(function () {
					stickyButtonWrapper.style.display = "none";
					popin.classList.remove("is-exiting");
					isAnimating = false;
				}, 300); // Durée de l'animation
			} else if (
				mobileButton &&
				!mobileButton.classList.contains("is-exiting")
			) {
				mobileButton.classList.add("is-exiting");
				setTimeout(function () {
					stickyButtonWrapper.style.display = "none";
					mobileButton.classList.remove("is-exiting");
					isAnimating = false;
				}, 300); // Durée de l'animation
			} else {
				stickyButtonWrapper.style.display = "none";
				isAnimating = false;
			}
		} else if (isAboveViewport && !footerVisible && !isCurrentlyVisible) {
			// Le bouton original est AU-DESSUS du viewport (l'utilisateur a scrollé en dessous) et le footer n'est pas visible, afficher le sticky avec animation d'entrée
			isAnimating = true;
			stickyButtonWrapper.style.display = "block";
			cloneButtonContent();

			// Retirer la classe is-exiting si elle existe
			if (popin) {
				popin.classList.remove("is-exiting");
			}
			if (mobileButton) {
				mobileButton.classList.remove("is-exiting");
			}

			// Forcer le reflow pour déclencher l'animation d'entrée
			setTimeout(function () {
				void stickyButtonWrapper.offsetHeight;
				isAnimating = false;
			}, 10);
		}
	}

	function handleScroll() {
		if (scrollTimeout) {
			clearTimeout(scrollTimeout);
		}

		scrollTimeout = setTimeout(function () {
			updateStickyButton();
		}, 10);
	}

	function initialize() {
		if (isInitialized) return;

		originalButton = findOriginalButton();
		stickyButtonWrapper = findStickyButton();

		if (!originalButton || !stickyButtonWrapper) {
			// Réessayer après un court délai si les éléments ne sont pas encore chargés
			setTimeout(initialize, 500);
			return;
		}

		// Récupérer les éléments de la popin desktop
		stickyPopin = stickyButtonWrapper.querySelector(
			".product-form__sticky-popin",
		);
		if (stickyPopin) {
			stickyPopinTitle = stickyPopin.querySelector(
				".product-form__sticky-popin-title",
			);
			stickyPopinFormat = stickyPopin.querySelector(
				".product-form__sticky-popin-format",
			);
			stickyPopinPrice = stickyPopin.querySelector(
				".product-form__sticky-popin-price",
			);
			stickyPopinImage = stickyPopin.querySelector(
				".product-form__sticky-popin-image img",
			);
		}

		// Récupérer le conteneur de contenu (pour mobile)
		stickyButtonContent = stickyButtonWrapper.querySelector(
			".product-form__sticky-button-mobile .product-form__sticky-button-content",
		);

		// Initialiser le contenu
		cloneButtonContent();

		// Mettre à jour les informations de la popin
		updateStickyPopinInfo();

		// Vérifier l'état initial
		updateStickyButton();

		// Écouter le scroll
		window.addEventListener("scroll", handleScroll, { passive: true });
		window.addEventListener("resize", handleScroll, { passive: true });

		// Écouter les changements de variante pour mettre à jour le bouton
		document.addEventListener("variant:change", function (event) {
			setTimeout(function () {
				originalButton = findOriginalButton();
				cloneButtonContent();
				updateStickyPopinInfo();
				updateStickyButton();
			}, 100);
		});

		// Écouter les re-renders du produit
		document.addEventListener("product:rerendered", function () {
			setTimeout(function () {
				originalButton = findOriginalButton();
				cloneButtonContent();
				updateStickyPopinInfo();
				updateStickyButton();
			}, 100);
		});

		isInitialized = true;
	}

	// Initialiser au chargement du DOM
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", initialize);
	} else {
		initialize();
	}

	// Réinitialiser si le contenu change dynamiquement
	const observer = new MutationObserver(function (mutations) {
		if (!isInitialized) {
			initialize();
		} else {
			// Vérifier si le bouton original a changé
			const newOriginalButton = findOriginalButton();
			if (newOriginalButton !== originalButton) {
				originalButton = newOriginalButton;
				cloneButtonContent();
				updateStickyButton();
			}
		}
	});

	observer.observe(document.body, {
		childList: true,
		subtree: true,
	});
})();
